/**
 * Granularities offered by the "Group data" radios of the date picker, and the
 * subset each route allows.
 *
 * The scraping tables behind Visibility are weekly (one row per week, keyed on
 * the Monday): Daily does not exist for that module. This is the single place
 * that rule lives — the date picker, the mobile filters drawer, the header and
 * the Visibility pages all go through it, so they cannot drift apart.
 */
export type Timestep = 'date_day' | 'year_week' | 'year_month';

const ALL_TIMESTEPS: readonly Timestep[] = ['date_day', 'year_week', 'year_month'];

// Finest first: the first entry is the fallback for a disallowed value.
const VISIBILITY_TIMESTEPS: readonly Timestep[] = ['year_week', 'year_month'];

const VISIBILITY_ROOT = '/brand/visibility';

export const isVisibilityPath = (pathname?: string | null): boolean =>
    Boolean(pathname && (pathname === VISIBILITY_ROOT || pathname.startsWith(`${VISIBILITY_ROOT}/`)));

export const getAllowedTimesteps = (pathname?: string | null): readonly Timestep[] =>
    isVisibilityPath(pathname) ? VISIBILITY_TIMESTEPS : ALL_TIMESTEPS;

export const isTimestepAllowed = (timestep: string | null | undefined, pathname?: string | null): boolean =>
    getAllowedTimesteps(pathname).includes(timestep as Timestep);

/** Granularity a Visibility query must use: Daily (or anything unknown) becomes Weekly. */
export const toVisibilityTimestep = (timestep: string | null | undefined): Timestep =>
    VISIBILITY_TIMESTEPS.includes(timestep as Timestep) ? (timestep as Timestep) : VISIBILITY_TIMESTEPS[0];

/** `timestep` clamped to what `pathname` allows; untouched outside Visibility. */
export const resolveTimestep = (timestep: string, pathname?: string | null): string =>
    isVisibilityPath(pathname) ? toVisibilityTimestep(timestep) : timestep;
